/**
 * Dashboard widget — entrevistas confirmadas pelo recrutador mas ainda
 * NÃO confirmadas pelo candidato (e que ainda vão acontecer).
 *
 * Mostra a lista compacta com ação inline pra reenviar o lembrete via
 * WhatsApp + e-mail. Útil pra recrutador caçar candidatos que esqueceram
 * de clicar no link de confirmação.
 *
 * Endpoints:
 *  - GET /api/interviews/pending-confirmations
 *  - POST /api/interviews/{id}/resend-reminder
 */
import axios from "axios";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  CalendarClock,
  Send,
  Loader2,
  Bell,
  CheckCircle2,
  AlertTriangle,
  ChevronRight,
} from "lucide-react";
import { toast } from "sonner";
import { API } from "@/context/AuthContext";

// Formato amigável tipo "Ter 27/05 às 14:00 (em 3h)"
const fmtCountdown = (iso) => {
  if (!iso) return "—";
  try {
    const dt = new Date(iso);
    const now = Date.now();
    const diffMin = Math.round((dt.getTime() - now) / 60000);
    const datePart = dt.toLocaleString("pt-BR", {
      weekday: "short",
      day: "2-digit",
      month: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
    let rel;
    if (diffMin < 60) rel = `em ${diffMin}min`;
    else if (diffMin < 60 * 24) rel = `em ${Math.round(diffMin / 60)}h`;
    else rel = `em ${Math.round(diffMin / (60 * 24))}d`;
    return { datePart, rel, diffMin };
  } catch {
    return { datePart: iso, rel: "", diffMin: 9999 };
  }
};

const PendingConfirmationsPanel = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [resending, setResending] = useState({});

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(`${API}/interviews/pending-confirmations`);
      setRows(res.data || []);
    } catch (err) {
      setError(err?.response?.data?.detail || "Erro ao carregar.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleResend = async (id) => {
    setResending((s) => ({ ...s, [id]: true }));
    try {
      const res = await axios.post(
        `${API}/interviews/${id}/resend-reminder`,
        {},
      );
      if (res.data?.success) {
        toast.success("Lembrete reenviado por WhatsApp e e-mail.");
        // Refresh para atualizar `last_manual_reminder_at`
        await load();
      } else {
        toast.warning(res.data?.message || "Não foi possível enviar.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao reenviar.");
    } finally {
      setResending((s) => ({ ...s, [id]: false }));
    }
  };

  // Hide widget when there's nothing to confirm — keeps the dashboard clean.
  if (!loading && !error && rows.length === 0) {
    return null;
  }

  return (
    <div
      data-testid="pending-confirmations-panel"
      className="bg-white border border-amber-200 rounded-xl shadow-sm overflow-hidden"
    >
      <div className="px-5 py-4 border-b border-amber-100 bg-gradient-to-r from-amber-50 to-white flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-lg bg-amber-100 text-amber-700 flex items-center justify-center">
            <CalendarClock className="w-5 h-5" strokeWidth={2.2} />
          </div>
          <div>
            <h2 className="heading text-base font-semibold text-slate-900">
              Confirmações pendentes
            </h2>
            <p className="text-xs text-slate-500">
              Candidatos que ainda não clicaram no link de confirmação
            </p>
          </div>
        </div>
        {!loading && rows.length > 0 && (
          <span
            data-testid="pending-confirmations-count"
            className="inline-flex items-center px-2.5 py-1 rounded-full bg-amber-100 text-amber-800 text-xs font-bold"
          >
            {rows.length} pendente{rows.length > 1 ? "s" : ""}
          </span>
        )}
      </div>

      {loading ? (
        <div className="px-5 py-6 flex items-center justify-center gap-2 text-sm text-slate-500">
          <Loader2 className="w-4 h-4 animate-spin" /> Carregando…
        </div>
      ) : error ? (
        <div className="px-5 py-4 text-sm text-rose-700 bg-rose-50">
          {error}
        </div>
      ) : (
        <ul className="divide-y divide-slate-100">
          {rows.map((row) => {
            const c = fmtCountdown(row.starts_at);
            const urgent = c.diffMin && c.diffMin < 60 * 24; // < 24h
            const lastSent = row.last_manual_reminder_at
              ? new Date(row.last_manual_reminder_at)
              : null;
            return (
              <li
                key={row.id}
                data-testid={`pending-row-${row.id}`}
                className="px-5 py-3 hover:bg-slate-50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div
                    className={`shrink-0 w-10 h-10 rounded-lg flex items-center justify-center ${
                      urgent
                        ? "bg-rose-100 text-rose-700"
                        : "bg-slate-100 text-slate-600"
                    }`}
                    title={urgent ? "Menos de 24h" : ""}
                  >
                    {urgent ? (
                      <AlertTriangle className="w-4 h-4" />
                    ) : (
                      <Bell className="w-4 h-4" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-semibold text-slate-900 truncate">
                        {row.candidate_name || "—"}
                      </span>
                      <span
                        className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${
                          urgent
                            ? "bg-rose-50 text-rose-700 border border-rose-200"
                            : "bg-amber-50 text-amber-700 border border-amber-200"
                        }`}
                      >
                        {c.rel}
                      </span>
                    </div>
                    <div className="text-[11px] text-slate-500 mt-0.5 flex items-center gap-1.5 flex-wrap">
                      <span className="truncate">{row.job_title || "—"}</span>
                      <ChevronRight className="w-3 h-3" />
                      <span>{c.datePart}</span>
                      {lastSent && (
                        <>
                          <span className="text-slate-300">·</span>
                          <span className="inline-flex items-center gap-1 text-emerald-600">
                            <CheckCircle2 className="w-3 h-3" />
                            Último reenvio: {lastSent.toLocaleString("pt-BR", {
                              day: "2-digit",
                              month: "2-digit",
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleResend(row.id)}
                    disabled={resending[row.id]}
                    data-testid={`resend-reminder-${row.id}`}
                    className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-amber-500 hover:bg-amber-600 disabled:opacity-60 text-white text-xs font-semibold transition-colors"
                    title="Reenvia o lembrete via WhatsApp + e-mail"
                  >
                    {resending[row.id] ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <Send className="w-3.5 h-3.5" />
                    )}
                    Reenviar
                  </button>
                  <Link
                    to={`/vagas/${row.job_id}`}
                    data-testid={`view-job-${row.id}`}
                    className="shrink-0 text-xs text-brand-blue hover:underline px-1.5"
                  >
                    Ver vaga
                  </Link>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default PendingConfirmationsPanel;
